// Client POV — facility edit form used by logged-in client users
/**
 * EditFacilityForm
 *
 * Self-contained form component for editing an existing facility.
 * Driven by the useEditFacility hook — all state and handlers are passed
 * as props so the component is fully testable without API calls.
 *
 * Renders:
 * - Read-only Facility Sequential ID (e.g. FACILITY000001)
 * - 10 editable fields: name, phone, countryCode, address, tax fields
 * - Loading skeleton while fetching initial data
 * - Field-level validation messages
 * - Cancel / Save buttons via CreateLayout
 * - Cancel confirmation dialog (built into CreateLayout)
 * - Error snackbar (built into CreateLayout)
 * - Tabs: Aparatura (disabled) and Pracownicy (employees)
 *
 * Why facilitySequentialId is separate from formData: the sequential ID is
 * assigned by the DB and is never editable; it must not be included in the
 * PATCH payload, so it lives outside the form data object.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { Plus } from 'react-feather';

import Loader from '@/app/routes/Loader';
import { TextField } from '@/shared/components/TextField';
import { Tooltip } from '@/shared/components/Tooltip/Tooltip';
import { Snackbar } from '@/shared/components/Snackbar';
import CreateLayout from '@/shared/layout/createLayout';

import { FACILITY_LABELS } from '../../constants/facilityLabels';
import { EMPLOYEE_ERROR_MESSAGES } from '../../errors';
import { EQUIPMENT_ERROR_MESSAGES as TL } from '../../errors/translations/errorMessages';
import plLabels from '../../constants/plLabels.json';
import {
  formatNip,
  validateEmployeeName,
  validateEmail,
  validateEmployeePhone,
} from '../../utils/validation';
import { createFinalizedEmployee } from '../../api/addEmployeeApiService';
import { finalizeFacility } from '../../api/addFacilityApiService';
import { AddEmployeeForm } from '../AddEmployeeForm';
import { AddEquipmentForm } from '../AddEquipmentForm';
import { EmployeeList } from '../EmployeeList/EmployeeList';
import { EquipmentList } from '../EquipmentList/EquipmentList';
import { useAddEquipment } from '../../hooks/useAddEquipment';
import EditFacilityAccessDeniedScreen from './EditFacilityAccessDeniedScreen';
import EditFacilityLoadErrorScreen from './EditFacilityLoadErrorScreen';
import type {
  EditFacilityFormData,
  EditFacilityFormErrors,
} from '../../types/editFacilityType';
import type {
  EmployeeEntry,
  EmployeeEntryErrors,
} from '../../types/addEmployeeType';

// ── Types ─────────────────────────────────────────────────────────────────────

type TabId = 'aparatura' | 'pracownicy';

interface EditFacilityFormProps {
  facilityId: string;
  clientId: string | null;
  formData: EditFacilityFormData;
  facilitySequentialId: string | null;
  isLoading: boolean;
  isLoadError: boolean;
  isSubmitting: boolean;
  isAccessDenied: boolean;
  errors: EditFacilityFormErrors;
  isCancelModalOpen: boolean;
  isSnackbarOpen: boolean;
  snackbarError: string;
  hasChanges: boolean;
  initialEmployeeCount: number;
  initialEquipmentCount: number;
  canListFacilityUser: boolean;
  canCreateFacilityUser: boolean;
  canEditFacilityUser: boolean;
  canCreateEquipment?: boolean;
  canEditEquipment?: boolean;
  canListEquipment: boolean;
  canToggleEmployeeStatus: boolean;
  isToggleDisabled?: boolean;
  toggleDisabledReason?: string | undefined;
  handleFieldChange: (field: keyof EditFacilityFormData, value: string) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleCancel: () => void;
  handleConfirmCancel: () => void;
  handleCloseCancelModal: () => void;
  handleCloseSnackbar: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const emptyEntry = (): EmployeeEntry => ({
  id: 'edit-facility-employee',
  name: '',
  email: '',
  phone: '',
  countryCode: '+48',
  academicTitle: '',
});

const emptyErrors = (): EmployeeEntryErrors => ({
  name: '',
  email: '',
  phone: '',
  gender: '',
  academicTitle: '',
});

// ── Component ─────────────────────────────────────────────────────────────────
export const EditFacilityForm: React.FC<EditFacilityFormProps> = ({
  facilityId,
  clientId,
  formData,
  facilitySequentialId,
  isLoading,
  isLoadError,
  isSubmitting,
  isAccessDenied,
  errors,
  isCancelModalOpen,
  isSnackbarOpen,
  snackbarError,
  hasChanges,
  initialEmployeeCount,
  initialEquipmentCount,
  canListFacilityUser,
  canCreateFacilityUser,
  canEditFacilityUser,
  canCreateEquipment = false,
  canEditEquipment = false,
  canListEquipment,
  canToggleEmployeeStatus,
  isToggleDisabled = false,
  toggleDisabledReason,
  handleFieldChange,
  handleSubmit,
  handleCancel,
  handleConfirmCancel,
  handleCloseCancelModal,
  handleCloseSnackbar,
}) => {
  // ── Tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabId>('aparatura');

  // ── Employee section state ─────────────────────────────────────────────────
  const [isAddingEmployee, setIsAddingEmployee] = useState(true);
  const [facilityUserTotalCount, setFacilityUserTotalCount] = useState(0);
  const [isEmployeeAccessDenied, setIsEmployeeAccessDenied] = useState(false);
  const [employeeRefreshKey, setEmployeeRefreshKey] = useState(0);
  const [localSubmittedCount, setLocalSubmittedCount] = useState(0);
  const [currentEntry, setCurrentEntry] = useState<EmployeeEntry>(emptyEntry);
  const [entryErrors, setEntryErrors] =
    useState<EmployeeEntryErrors>(emptyErrors);
  const [isSubmittingEmployee, setIsSubmittingEmployee] = useState(false);
  const [isEmployeeSuccessOpen, setIsEmployeeSuccessOpen] = useState(false);
  const [employeeApiError, setEmployeeApiError] = useState('');
  const [isEmployeeErrorOpen, setIsEmployeeErrorOpen] = useState(false);
  const [hasEmployeeChange, setHasEmployeeChange] = useState(false);

  // ── Equipment section state ────────────────────────────────────────────────
  const [isAddingEquipment, setIsAddingEquipment] = useState(true);
  const [equipmentTotalCount, setEquipmentTotalCount] = useState(0);
  const [equipmentRefreshKey, setEquipmentRefreshKey] = useState(0);
  const [isEquipmentAccessDenied, setIsEquipmentAccessDenied] = useState(false);
  const [hasEquipmentChange, setHasEquipmentChange] = useState(false);
  const [isEmployeeEditDirty, setIsEmployeeEditDirty] = useState(false);
  const [isEquipmentEditDirty, setIsEquipmentEditDirty] = useState(false);
  const [isEmployeeEditSaving, setIsEmployeeEditSaving] = useState(false);
  const [isEquipmentEditSaving, setIsEquipmentEditSaving] = useState(false);
  const [employeeResetKey, setEmployeeResetKey] = useState(0);
  const [equipmentResetKey, setEquipmentResetKey] = useState(0);

  // Single sync effect — runs once when the loader resolves.
  // All permission-aware initial state is set here in one batch so the form
  // renders exactly once with the correct tab and counts (no visible flash).
  useEffect(() => {
    if (!isLoading) {
      setFacilityUserTotalCount(canListFacilityUser ? initialEmployeeCount : 0);
      setIsAddingEmployee(initialEmployeeCount === 0 || !canListFacilityUser);
      setEquipmentTotalCount(canListEquipment ? initialEquipmentCount : 0);
      setIsAddingEquipment(initialEquipmentCount === 0 || !canListEquipment);
      // Both denied → stay on aparatura (safe default). Only switch when
      // equipment is denied but employees are still accessible.
      setActiveTab(
        !canListEquipment && canListFacilityUser ? 'pracownicy' : 'aparatura'
      );
    }
  }, [
    isLoading,
    initialEmployeeCount,
    initialEquipmentCount,
    canListEquipment,
    canListFacilityUser,
  ]);

  const {
    equipmentList,
    errorsList: equipmentErrorsList,
    uploadProgressList: equipmentUploadProgressList,
    failedUploadFilesList: equipmentFailedUploadFilesList,
    isSubmitting: isEquipmentSubmitting,
    addEntry: addEquipmentEntry,
    removeEntry: removeEquipmentEntry,
    updateField: updateEquipmentField,
    updateCategory: updateEquipmentCategory,
    updateDateOfProduction: updateEquipmentDateOfProduction,
    handleImageChange: handleEquipmentImageChange,
    handleImageError: handleEquipmentImageError,
    handleDocsChange: handleEquipmentDocsChange,
    handleDocsError: handleEquipmentDocsError,
    retryUpload: retryEquipmentUpload,
    submitEntry: submitEquipmentEntry,
    setEntrySerialError,
  } = useAddEquipment();

  const [isEquipmentFinalizing, setIsEquipmentFinalizing] = useState(false);
  const [isEquipmentSuccessOpen, setIsEquipmentSuccessOpen] = useState(false);

  const handleEquipmentSubmit = useCallback(async () => {
    const entry = equipmentList[0];
    if (!entry || !clientId) {
      return;
    }
    const result = await submitEquipmentEntry(entry.id, facilityId, clientId);
    if (!result.success) {
      return;
    }

    setIsEquipmentFinalizing(true);
    try {
      const finalizeResult = await finalizeFacility(clientId, facilityId);
      if (finalizeResult.success) {
        removeEquipmentEntry(entry.id);
        setEquipmentRefreshKey((prev) => prev + 1);
        setHasEquipmentChange(true);
        setIsAddingEquipment(false);
        setIsEquipmentSuccessOpen(true);
      } else if (finalizeResult.serialNumberConflict) {
        setEntrySerialError(entry.id, TL.SERIAL_NUMBER_DUPLICATE.polish);
      }
    } finally {
      setIsEquipmentFinalizing(false);
    }
  }, [
    equipmentList,
    clientId,
    facilityId,
    submitEquipmentEntry,
    removeEquipmentEntry,
    setEntrySerialError,
  ]);

  const handleEquipmentCancel = useCallback(() => {
    const entry = equipmentList[0];
    if (entry) {
      removeEquipmentEntry(entry.id);
    }
    setIsAddingEquipment(false);
  }, [equipmentList, removeEquipmentEntry]);

  const handleTabChange = useCallback(
    (tab: TabId) => {
      if (activeTab === 'pracownicy' && tab !== 'pracownicy') {
        setCurrentEntry(emptyEntry());
        setEntryErrors(emptyErrors());
        if (localSubmittedCount > 0 || facilityUserTotalCount > 0) {
          setIsAddingEmployee(false);
        }
        setEmployeeResetKey((prev) => prev + 1);
      }
      if (activeTab === 'aparatura' && tab !== 'aparatura') {
        const entry = equipmentList[0];
        if (entry) {
          updateEquipmentField(entry.id, 'name', '');
          updateEquipmentField(entry.id, 'description', '');
          updateEquipmentField(entry.id, 'serialNumber', '');
          updateEquipmentField(entry.id, 'modelName', '');
          updateEquipmentCategory(entry.id, '');
          updateEquipmentDateOfProduction(entry.id, undefined);
          handleEquipmentImageChange(entry.id, []);
          handleEquipmentDocsChange(entry.id, []);
        }
        if (equipmentTotalCount > 0) {
          setIsAddingEquipment(false);
        }
        setEquipmentResetKey((prev) => prev + 1);
      }
      setActiveTab(tab);
    },
    [
      activeTab,
      equipmentList,
      equipmentTotalCount,
      facilityUserTotalCount,
      localSubmittedCount,
      updateEquipmentField,
      updateEquipmentCategory,
      updateEquipmentDateOfProduction,
      handleEquipmentImageChange,
      handleEquipmentDocsChange,
    ]
  );

  const handleFacilityUserTotalCountChange = useCallback((count: number) => {
    setFacilityUserTotalCount(count);
  }, []);

  const handleEmployeeAccessDenied = useCallback(() => {
    setIsEmployeeAccessDenied(true);
  }, []);

  const handleEquipmentAccessDenied = useCallback(() => {
    setIsEquipmentAccessDenied(true);
  }, []);

  const [isEquipmentEditSuccessOpen, setIsEquipmentEditSuccessOpen] =
    useState(false);

  const handleEquipmentEdited = useCallback(() => {
    setEquipmentRefreshKey((prev) => prev + 1);
    setHasEquipmentChange(true);
    setIsEquipmentEditSuccessOpen(true);
  }, []);

  const handleEmployeeFieldChange = (
    field: keyof Pick<
      EmployeeEntry,
      'name' | 'email' | 'phone' | 'academicTitle'
    >,
    value: string
  ) => {
    setCurrentEntry((prev) => ({ ...prev, [field]: value }));
    if (entryErrors[field]) {
      setEntryErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleGenderChange = (value: 'male' | 'female' | undefined) => {
    const { gender: _g, ...rest } = currentEntry;
    setCurrentEntry(
      value !== undefined
        ? ({ ...rest, gender: value } satisfies EmployeeEntry)
        : (rest as EmployeeEntry)
    );
  };

  const handleEmployeeSubmit = async () => {
    const errs: EmployeeEntryErrors = {
      name: validateEmployeeName(currentEntry.name),
      email: validateEmail(currentEntry.email),
      phone: validateEmployeePhone(currentEntry.phone),
      gender: '',
      academicTitle: '',
    };
    if (Object.values(errs).some(Boolean)) {
      setEntryErrors(errs);
      return;
    }

    setIsSubmittingEmployee(true);
    const finalizedPayload = {
      name: currentEntry.name.trim(),
      email: currentEntry.email.trim(),
      phoneNumber: currentEntry.phone,
      countryCode: currentEntry.countryCode,
      ...(currentEntry.gender !== undefined && { gender: currentEntry.gender }),
      ...(currentEntry.academicTitle?.trim()
        ? { academicTitle: currentEntry.academicTitle.trim() }
        : {}),
    };
    const result = await createFinalizedEmployee(facilityId, finalizedPayload);

    setIsSubmittingEmployee(false);

    if (!result.success) {
      if (result.error === EMPLOYEE_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS) {
        setEntryErrors((prev) => ({
          ...prev,
          email: result.error ?? EMPLOYEE_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS,
        }));
      } else if (result.error === EMPLOYEE_ERROR_MESSAGES.ACCESS_DENIED) {
        setIsEmployeeAccessDenied(true);
      } else {
        setEmployeeApiError(
          result.error ?? EMPLOYEE_ERROR_MESSAGES.DEFAULT_ERROR
        );
        setIsEmployeeErrorOpen(true);
      }
      return;
    }

    setCurrentEntry(emptyEntry());
    setEntryErrors(emptyErrors());
    setLocalSubmittedCount((prev) => prev + 1);
    setEmployeeRefreshKey((prev) => prev + 1);
    setIsAddingEmployee(false);
    setIsEmployeeSuccessOpen(true);
    setHasEmployeeChange(true);
  };

  const handleEmployeeCancel = () => {
    setCurrentEntry(emptyEntry());
    setEntryErrors(emptyErrors());
    if (localSubmittedCount > 0 || facilityUserTotalCount > 0) {
      setIsAddingEmployee(false);
    }
  };

  // ── Early returns ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center bg-white h-screen w-full"
        data-testid="edit-facility-loader"
      >
        <Loader />
      </div>
    );
  }

  if (isLoadError) {
    return <EditFacilityLoadErrorScreen />;
  }

  if (isAccessDenied) {
    return <EditFacilityAccessDeniedScreen />;
  }

  const isEquipmentTabDisabled = isEquipmentAccessDenied || !canListEquipment;
  const isEmployeeTabDisabled = isEmployeeAccessDenied || !canListFacilityUser;
  const isAnyChildSaving =
    isSubmittingEmployee ||
    isEquipmentSubmitting ||
    isEquipmentFinalizing ||
    isEmployeeEditSaving ||
    isEquipmentEditSaving;
  const hasAnyEmployee = localSubmittedCount > 0 || facilityUserTotalCount > 0;

  const activeEquipmentEntry = equipmentList[0];
  const activeEquipmentErrors = equipmentErrorsList[0];
  const activeEquipmentUploadProgress = equipmentUploadProgressList[0];
  const activeEquipmentFailedUploads = equipmentFailedUploadFilesList[0];

  const isEmployeeAddDirty =
    isAddingEmployee &&
    (!!currentEntry.name ||
      !!currentEntry.email ||
      !!currentEntry.phone ||
      currentEntry.gender !== undefined ||
      !!currentEntry.academicTitle);

  const isEquipmentAddDirty =
    isAddingEquipment &&
    !!activeEquipmentEntry &&
    (!!activeEquipmentEntry.name ||
      !!activeEquipmentEntry.description ||
      !!activeEquipmentEntry.imageFile ||
      activeEquipmentEntry.docFiles.length > 0);

  const hasUnsavedChildChanges =
    isEmployeeAddDirty ||
    isEquipmentAddDirty ||
    isEmployeeEditDirty ||
    isEquipmentEditDirty;

  return (
    <>
      <CreateLayout
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCancelModalOpen={isCancelModalOpen}
        handleCloseCancelModal={handleCloseCancelModal}
        handleConfirmCancel={handleConfirmCancel}
        isSnackbarOpen={isSnackbarOpen}
        handleCloseSnackbar={handleCloseSnackbar}
        snackbarError={snackbarError}
        submitButtonLabel={plLabels.editFacility.form.submitButton}
        submitButtonLoadingLabel={
          plLabels.editFacility.form.submitButtonLoading
        }
        cancelButtonLabel={FACILITY_LABELS.CANCEL_BUTTON}
        submitButtonDisabled={
          isSubmitting ||
          hasUnsavedChildChanges ||
          !(hasChanges || hasEmployeeChange || hasEquipmentChange)
        }
        Header={() => (
          <h1 className="text-[22px] font-normal leading-7 text-[#181A1B]">
            Edycja placówki
          </h1>
        )}
      >
        <React.Fragment key={facilityId}>
          <div className="rounded-[12px] flex flex-col gap-8">
            {/* ── Basic details ────────────────────────────────────── */}
            <div className="flex flex-col gap-2">
              <p className="text-[18px] font-normal text-on-surface-variant">
                {FACILITY_LABELS.SECTION_BASIC_DETAILS}
              </p>

              <div className="flex gap-4">
                <div className="flex-1">
                  <TextField
                    fullWidth
                    label={FACILITY_LABELS.FIELD_EDIT_NAME}
                    dataTestId="edit-facility-name"
                    autoTrimMaxLength={200}
                    value={formData.name}
                    onChange={(e) =>
                      handleFieldChange('name', e.target.value.trimStart())
                    }
                    error={Boolean(errors.name)}
                    errorMessage={errors.name ?? ''}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1">
                  <TextField
                    fullWidth
                    label={FACILITY_LABELS.FIELD_PHONE}
                    dataTestId="edit-facility-phoneNumber"
                    type="tel"
                    maxLength={9}
                    value={formData.phoneNumber}
                    onChange={(e) =>
                      handleFieldChange(
                        'phoneNumber',
                        e.target.value.replace(/\D/g, '').slice(0, 9)
                      )
                    }
                    error={Boolean(errors.phoneNumber)}
                    errorMessage={errors.phoneNumber ?? ''}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1">
                  <TextField
                    fullWidth
                    label={FACILITY_LABELS.FIELD_FACILITY_ID}
                    value={facilitySequentialId ?? ''}
                    disabled
                    dataTestId="edit-facility-sequential-id"
                  />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            {/* ── Address ──────────────────────────────────────────── */}
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                {FACILITY_LABELS.SECTION_ADDRESS}
              </p>

              <div className="flex flex-col gap-8">
                {/* Row: Address1 | Address2 | City */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <TextField
                      fullWidth
                      label={FACILITY_LABELS.FIELD_ADDRESS1}
                      dataTestId="edit-facility-addressLine1"
                      autoTrimMaxLength={200}
                      value={formData.addressLine1}
                      onChange={(e) =>
                        handleFieldChange(
                          'addressLine1',
                          e.target.value.trimStart()
                        )
                      }
                      error={Boolean(errors.addressLine1)}
                      errorMessage={errors.addressLine1 ?? ''}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      fullWidth
                      label={
                        <>
                          {FACILITY_LABELS.FIELD_ADDRESS2}
                          <span className="text-bodySmall font-normal">
                            {' '}
                            {FACILITY_LABELS.FIELD_OPTIONAL}
                          </span>
                        </>
                      }
                      dataTestId="edit-facility-addressLine2"
                      autoTrimMaxLength={200}
                      value={formData.addressLine2}
                      onChange={(e) =>
                        handleFieldChange(
                          'addressLine2',
                          e.target.value.trimStart()
                        )
                      }
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      fullWidth
                      label={FACILITY_LABELS.FIELD_CITY}
                      dataTestId="edit-facility-city"
                      autoTrimMaxLength={200}
                      value={formData.city}
                      onChange={(e) =>
                        handleFieldChange('city', e.target.value.trimStart())
                      }
                      error={Boolean(errors.city)}
                      errorMessage={errors.city ?? ''}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Row: State | Zip | Country */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <TextField
                      fullWidth
                      label={FACILITY_LABELS.FIELD_STATE}
                      dataTestId="edit-facility-state"
                      autoTrimMaxLength={200}
                      value={formData.state}
                      onChange={(e) =>
                        handleFieldChange('state', e.target.value.trimStart())
                      }
                      error={Boolean(errors.state)}
                      errorMessage={errors.state ?? ''}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      fullWidth
                      label={
                        <>
                          {FACILITY_LABELS.FIELD_ZIP}
                          <span className="text-bodySmall font-normal">
                            {' '}
                            {FACILITY_LABELS.FIELD_OPTIONAL}
                          </span>
                        </>
                      }
                      dataTestId="edit-facility-zipCode"
                      maxLength={6}
                      value={formData.zipCode}
                      onChange={(e) =>
                        handleFieldChange('zipCode', e.target.value.trimStart())
                      }
                      error={Boolean(errors.zipCode)}
                      errorMessage={errors.zipCode ?? ''}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      fullWidth
                      label={FACILITY_LABELS.FIELD_COUNTRY}
                      dataTestId="edit-facility-country"
                      value={formData.country}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            {/* ── Tax ──────────────────────────────────────────────── */}
            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                {FACILITY_LABELS.SECTION_TAX}
              </p>

              <div className="flex gap-4">
                <div className="flex-1">
                  <TextField
                    fullWidth
                    label={FACILITY_LABELS.FIELD_NIP}
                    dataTestId="edit-facility-nip"
                    autoTrimMaxLength={13}
                    value={formData.nip}
                    onChange={(e) =>
                      handleFieldChange('nip', formatNip(e.target.value))
                    }
                    error={Boolean(errors.nip)}
                    errorMessage={errors.nip ?? ''}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-[2]" />
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            {/* ── Tabs: Aparatura / Pracownicy ──────────────────────── */}
            <div className="flex flex-col gap-6">
              {/* Tab bar row */}
              <div className="flex items-center justify-between">
                <div className="flex bg-[#F2F4F5] rounded-lg p-1 w-[432px] gap-1">
                  {[
                    {
                      id: 'aparatura' as TabId,
                      label: `${FACILITY_LABELS.SECTION_EQUIPMENT} (${isEquipmentTabDisabled ? 0 : equipmentTotalCount})`,
                      isDisabled: isEquipmentTabDisabled || isAnyChildSaving,
                      tooltip: isEquipmentTabDisabled
                        ? plLabels.common.noPermission
                        : null,
                    },
                    {
                      id: 'pracownicy' as TabId,
                      label: `Pracownicy (${isEmployeeTabDisabled ? 0 : facilityUserTotalCount})`,
                      isDisabled: isEmployeeTabDisabled || isAnyChildSaving,
                      tooltip: plLabels.common.noPermission,
                    },
                  ].map((tab) => {
                    const button = (
                      <button
                        type="button"
                        data-testid={`facility-tab-${tab.id}`}
                        onClick={() => handleTabChange(tab.id)}
                        disabled={tab.isDisabled}
                        className={[
                          'w-full py-1.5 rounded-md text-titleMedium font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]',
                          activeTab === tab.id
                            ? 'bg-white text-primary-color-40 shadow-sm'
                            : 'text-outline hover:text-on-surface-variant',
                          tab.isDisabled
                            ? 'opacity-38 cursor-not-allowed'
                            : 'cursor-pointer',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {tab.label}
                      </button>
                    );

                    return (
                      <div key={tab.id} className="flex-1">
                        {tab.isDisabled && tab.tooltip ? (
                          <Tooltip content={tab.tooltip} forceShow>
                            {button}
                          </Tooltip>
                        ) : (
                          button
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Add employee button — inline with tab bar */}
                {activeTab === 'pracownicy' &&
                  canCreateFacilityUser &&
                  !isEmployeeAccessDenied &&
                  hasAnyEmployee &&
                  !isAddingEmployee && (
                    <button
                      type="button"
                      data-testid="Dodaj pracownika"
                      onClick={() => setIsAddingEmployee(true)}
                      className="flex items-center gap-2 border border-primary-color-40 text-primary-color-40 rounded-[8px] px-4 py-[7px] text-[14px] font-medium leading-5 cursor-pointer hover:bg-primary-color-40/[0.08] disabled:opacity-38 disabled:cursor-not-allowed"
                      disabled={isSubmittingEmployee}
                    >
                      <Plus size={20} />
                      {FACILITY_LABELS.ADD_EMPLOYEE_BUTTON}
                    </button>
                  )}
                {activeTab === 'aparatura' &&
                  canCreateEquipment &&
                  !isEquipmentAccessDenied &&
                  equipmentTotalCount > 0 &&
                  !isAddingEquipment && (
                    <button
                      type="button"
                      onClick={() => {
                        addEquipmentEntry();
                        setIsAddingEquipment(true);
                      }}
                      className="flex items-center gap-2 border border-primary-color-40 text-primary-color-40 rounded-[8px] px-4 py-[7px] text-[14px] font-medium leading-5 cursor-pointer hover:bg-primary-color-40/[0.08] disabled:opacity-38 disabled:cursor-not-allowed"
                      disabled={isEquipmentSubmitting}
                    >
                      <Plus size={20} />
                      {FACILITY_LABELS.ADD_EQUIPMENT_BUTTON_PRIMARY}
                    </button>
                  )}
              </div>

              {/* Aparatura tab content */}
              <div
                hidden={activeTab !== 'aparatura'}
                className={
                  activeTab !== 'aparatura' ? 'hidden' : 'flex flex-col gap-4'
                }
              >
                {!isEquipmentTabDisabled && (
                  <>
                    {isAddingEquipment &&
                    canCreateEquipment &&
                    activeEquipmentEntry &&
                    activeEquipmentErrors ? (
                      <AddEquipmentForm
                        entry={activeEquipmentEntry}
                        errors={activeEquipmentErrors}
                        isSubmitting={
                          isEquipmentSubmitting || isEquipmentFinalizing
                        }
                        uploadProgress={activeEquipmentUploadProgress}
                        failedUploadFiles={activeEquipmentFailedUploads}
                        onFieldChange={(field, value) =>
                          updateEquipmentField(
                            activeEquipmentEntry.id,
                            field,
                            value
                          )
                        }
                        onImageChange={(files) =>
                          handleEquipmentImageChange(
                            activeEquipmentEntry.id,
                            files
                          )
                        }
                        onImageError={(msg) =>
                          handleEquipmentImageError(
                            activeEquipmentEntry.id,
                            msg
                          )
                        }
                        onDocsChange={(files) =>
                          handleEquipmentDocsChange(
                            activeEquipmentEntry.id,
                            files
                          )
                        }
                        onDocsError={(msg) =>
                          handleEquipmentDocsError(activeEquipmentEntry.id, msg)
                        }
                        onRetry={(filename) =>
                          retryEquipmentUpload(
                            activeEquipmentEntry.id,
                            filename
                          )
                        }
                        onSubmit={() => void handleEquipmentSubmit()}
                        onCancel={handleEquipmentCancel}
                        canClose={equipmentTotalCount > 0}
                        onCategoryChange={(value) =>
                          updateEquipmentCategory(
                            activeEquipmentEntry.id,
                            value
                          )
                        }
                        onDateOfProductionChange={(date) =>
                          updateEquipmentDateOfProduction(
                            activeEquipmentEntry.id,
                            date
                          )
                        }
                      />
                    ) : (
                      <EquipmentList
                        clientId={clientId}
                        facilityId={facilityId}
                        refreshKey={equipmentRefreshKey}
                        resetKey={equipmentResetKey}
                        canEdit={canEditEquipment}
                        onTotalCountChange={setEquipmentTotalCount}
                        onAccessDenied={handleEquipmentAccessDenied}
                        onEquipmentEdited={handleEquipmentEdited}
                        onEditingChange={setIsEquipmentEditDirty}
                        onSavingChange={setIsEquipmentEditSaving}
                      />
                    )}
                  </>
                )}
              </div>

              {/* Pracownicy tab content */}
              <div
                hidden={activeTab !== 'pracownicy'}
                className={
                  activeTab !== 'pracownicy' ? 'hidden' : 'flex flex-col gap-4'
                }
              >
                {!isEmployeeTabDisabled && (
                  <>
                    {isAddingEmployee && canCreateFacilityUser ? (
                      <AddEmployeeForm
                        entry={currentEntry}
                        errors={entryErrors}
                        isSubmitting={isSubmittingEmployee}
                        cancelAlwaysEnabled={hasAnyEmployee}
                        onFieldChange={handleEmployeeFieldChange}
                        onGenderChange={handleGenderChange}
                        onSubmit={() => void handleEmployeeSubmit()}
                        onCancel={handleEmployeeCancel}
                      />
                    ) : (
                      <EmployeeList
                        key={employeeRefreshKey}
                        facilityId={facilityId}
                        refreshKey={employeeRefreshKey}
                        resetKey={employeeResetKey}
                        canEdit={canEditFacilityUser}
                        canToggle={canToggleEmployeeStatus}
                        isToggleDisabled={isToggleDisabled}
                        toggleDisabledReason={toggleDisabledReason}
                        isValid={true}
                        onTotalCountChange={handleFacilityUserTotalCountChange}
                        onAccessDenied={handleEmployeeAccessDenied}
                        onEmployeeEdited={() => setHasEmployeeChange(true)}
                        onEditingChange={setIsEmployeeEditDirty}
                        onSavingChange={setIsEmployeeEditSaving}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </React.Fragment>
      </CreateLayout>

      <Snackbar
        open={isEmployeeSuccessOpen}
        message={FACILITY_LABELS.ADD_EMPLOYEE_SUCCESS}
        variant="success"
        onClose={() => setIsEmployeeSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isEquipmentSuccessOpen}
        message={FACILITY_LABELS.ADD_EQUIPMENT_SUCCESS}
        variant="success"
        onClose={() => setIsEquipmentSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isEquipmentEditSuccessOpen}
        message={FACILITY_LABELS.EDIT_EQUIPMENT_SUCCESS}
        variant="success"
        onClose={() => setIsEquipmentEditSuccessOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={isEmployeeErrorOpen}
        message={employeeApiError}
        variant="error"
        onClose={() => setIsEmployeeErrorOpen(false)}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};
